import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { ILoginContext } from 'context';
import { useAppFeatureFlags, useLogin } from 'hooks';
import { ClinicSelector } from '../ClinicSelector';

import styles from './SelectClinicForm.module.scss';

const SELECTED_CLINIC_KEY_PREFIX = 'login:lastSelectedClinicId:';

function getSelectedClinicStorageKey(email: string): string {
  return `${SELECTED_CLINIC_KEY_PREFIX}${email.trim().toLowerCase()}`;
}

function getStoredClinicId(email: string): string | null {
  try {
    return localStorage.getItem(getSelectedClinicStorageKey(email));
  } catch {
    return null;
  }
}

function setStoredClinicId(email: string, clinicId: string): void {
  try {
    localStorage.setItem(getSelectedClinicStorageKey(email), clinicId);
  } catch {
    // Ignore storage failures so login still works in restricted browsers.
  }
}

export function SelectClinicForm() {
  const { t } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { displayIconInOrganizationSelectionDropdownFeature } = useAppFeatureFlags();
  const { enqueueSnackbar } = useSnackbar();
  const { loginModelData, onSelectClinic }: ILoginContext = useLogin();
  const storedClinicId = loginModelData.email ? getStoredClinicId(loginModelData.email) : null;
  const defaultClinicId =
    loginModelData.clinics?.find((clinic) => clinic.clinicId === storedClinicId)?.clinicId ??
    loginModelData.clinics?.[0]?.clinicId ??
    '';

  const schema = useMemo(
    () =>
      Yup.object().shape({
        clinicId: Yup.string().required(t('selectOrganizationRequired')),
      }),
    [t],
  );

  const form = useForm<{ clinicId: string }>({
    resolver: yupResolver(schema),
    defaultValues: { clinicId: defaultClinicId },
    mode: 'all',
  });

  const handleLogin = (): void => {
    if (form.formState.isValid) {
      const clinicId = form.getValues().clinicId;

      if (loginModelData.email) {
        setStoredClinicId(loginModelData.email, clinicId);
      }

      onSelectClinic(clinicId).catch((error) => {
        enqueueSnackbar(error, { variant: 'error' });
      });
    }
  };

  return (
    <div className={styles.form}>
      <Typography variant={isMobile ? 'h2' : 'h1'} component={isMobile ? 'h2' : 'h1'}>
        {t('selectOrganizationTitle')}
      </Typography>

      <Typography variant='body2' component='span'>
        {t('selectOrganizationDescription')}
      </Typography>

      <Stack className={styles.row}>
        <ClinicSelector
          name='clinicId'
          control={form.control}
          options={loginModelData.clinics!}
          displayIcon={displayIconInOrganizationSelectionDropdownFeature}
        />
      </Stack>

      <Stack className={styles.row} alignItems='flex-end'>
        <Button
          variant='contained'
          onClick={handleLogin}
          disabled={!form.formState.isValid}
          className={styles.formButton}
          type='submit'
        >
          {t('continue')}
        </Button>
      </Stack>
    </div>
  );
}
